import axios from "axios";

const instance = axios.create({
  // In production we serve the React build from the backend, so same-origin "/api" is safest.
  // In development you can set REACT_APP_API_BASE_URL=http://localhost:5002/api
  baseURL: process.env.REACT_APP_API_BASE_URL || "/api",
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Per-tab Tenant Session Helper:
 * Prefers sessionStorage (strictly isolated per browser tab) so logging into User B
 * in Tab 2 never hijacks User A's active session in Tab 1.
 */
export function getActiveAuth() {
  let token = sessionStorage.getItem("token");
  let role = sessionStorage.getItem("role");
  let userId = sessionStorage.getItem("userId");

  if (!token) {
    token = localStorage.getItem("token");
    role = localStorage.getItem("role");
    userId = localStorage.getItem("userId");
    if (token) {
      sessionStorage.setItem("token", token);
      if (role) sessionStorage.setItem("role", role);
      if (userId) sessionStorage.setItem("userId", userId);
    }
  }

  return { token, role, userId };
}

export function clearActiveAuth() {
  localStorage.removeItem("token");
  localStorage.removeItem("role");
  localStorage.removeItem("userId");
  sessionStorage.clear();
}

// CSRF token helper (used for sensitive actions)
export async function getCsrfToken(forceRefresh = false) {
  try {
    const existing = sessionStorage.getItem("csrfToken");
    if (existing && !forceRefresh) return existing;

    const res = await instance.get("/auth/csrf-token");
    const token = res?.data?.csrfToken;
    if (token) {
      sessionStorage.setItem("csrfToken", token);
      return token;
    }
  } catch (_) {
    // ignore; caller will handle missing token
  }
  return null;
}

// Request Interceptor: Attach Token + strict X-Tenant-Id header on every request
instance.interceptors.request.use(
  (config) => {
    const { token, userId } = getActiveAuth();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    if (userId) {
      config.headers["X-Tenant-Id"] = String(userId);
    }

    // If we're sending multipart FormData, do NOT force JSON content-type.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      try {
        config.headers?.delete?.("Content-Type");
      } catch (_) {
        if (config.headers) {
          delete config.headers["Content-Type"];
          delete config.headers["content-type"];
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Agar token expire ya tenant mismatch ho jaye toh logout kar de
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const url = String(error?.config?.url || "");

    const is2faRequired = Boolean(data?.requires2fa);
    const isLoginCall = url.includes("/auth/login");

    if (status === 401 && !is2faRequired && !isLoginCall) {
      clearActiveAuth();
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

export default instance;
